import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getRecommendations } from './request/request';
import { RecsData } from './Srv6Types';
import InlineSpinner from './InlineSpinner';
import ErrorBanner from './ErrorBanner';
import SeverityBadge from './SeverityBadge';
import Tooltip from '../Topology/components/Tooltip';
import FreshnessBar from './FreshnessBar';

interface Props {
    timeRange: string;
    onNavigate?: (tab: string, source?: string) => void;
}

const RULE_META: Record<string, { label: string; tooltip: string }> = {
    R1:  { label: 'SRv6 Throughput', tooltip: 'Router peak SRv6 throughput is near the configured capacity threshold.' },
    R2:  { label: 'Unused Algo',     tooltip: 'A flex-algo has zero traffic on multiple routers; candidate for removal.' },
    R3:  { label: 'Concentrated',    tooltip: 'A single flow carries >60% of a router\'s total SRv6 output.' },
    R4:  { label: 'Top Talker',      tooltip: 'Top 5 highest-throughput flows across the network (baseline reference).' },
    R5:  { label: 'Flow Drift',      tooltip: 'A flow changed >50% vs 1 hour ago with at least 5 Mbps absolute delta.' },
    R6:  { label: 'Silence',         tooltip: 'A flow that averaged >1 Mbps over 2 hours dropped to zero.' },
    R7:  { label: 'ECMP Skew',       tooltip: 'ECMP paths are imbalanced; one path carries most of the traffic.' },
    R8:  { label: 'Throughput',      tooltip: 'Average SRv6 output per router (informational baseline for planning).' },
    R9:  { label: 'Jitter',          tooltip: 'ISIS link jitter (max_delay - min_delay) exceeds 5 ms.' },
    R10: { label: 'Pkt Loss',        tooltip: 'Packet loss detected on an ISIS interface.' },
    R11: { label: 'Adjacency',       tooltip: 'Router has fewer ISIS adjacencies than the network median.' },
    R12: { label: 'Redundancy',      tooltip: 'Router has a single ISIS adjacency (no link redundancy).' },
};

function extractSource(link: string): string | undefined {
    try {
        const url = new URL(link, 'http://localhost');
        return url.searchParams.get('source') || undefined;
    } catch {
        return undefined;
    }
}

function extractTab(link: string): string {
    try {
        const url = new URL(link, 'http://localhost');
        return url.searchParams.get('tab') || 'traffic';
    } catch {
        return 'traffic';
    }
}

const Srv6RecommendationsTab: React.FC<Props> = ({ timeRange, onNavigate }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [recsData, setRecsData] = useState<RecsData | null>(null);
    const [lastFetched, setLastFetched] = useState<Date | null>(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = await getAccessTokenSilently();
            const res = await getRecommendations(token, { timeRange });
            if (res.data?.data) {
                setRecsData(res.data.data);
                setLastFetched(new Date());
            }
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to load recommendations');
        } finally {
            setIsLoading(false);
        }
    }, [getAccessTokenSilently, timeRange]);

    useEffect(() => { load(); }, [load]);

    if (isLoading) return <InlineSpinner />;
    if (error) return <ErrorBanner message={error} onRetry={load} />;

    return (
        <div className="flex flex-col gap-4 overflow-auto">
            <div className="flex items-start justify-between gap-4">
                <p className="text-[11px] text-semantic-text-secondary leading-relaxed">
                    Proactive observations for optimization and capacity planning. Hover over a rule badge to see what it checks.
                </p>
                <FreshnessBar lastFetched={lastFetched} onRefresh={load} />
            </div>
            {recsData && (
                <div className="flex gap-4 text-xs text-semantic-text-secondary items-center">
                    <SeverityBadge severity="high" className="inline-flex items-center gap-1" />
                    <span>{recsData.summary.high || 0}</span>
                    <SeverityBadge severity="medium" className="inline-flex items-center gap-1" />
                    <span>{recsData.summary.medium || 0}</span>
                    <SeverityBadge severity="info" className="inline-flex items-center gap-1" />
                    <span>{recsData.summary.info || 0}</span>
                    <span className="ml-auto">{recsData.summary.total || 0} total</span>
                </div>
            )}

            <div className="overflow-auto rounded-xl border border-semantic-border-secondary bg-semantic-bg-primary p-4">
                <table className="w-full text-xs border-collapse">
                    <thead>
                        <tr className="border-b border-semantic-border-secondary bg-semantic-bg-secondary">
                            <th className="text-left py-2 px-3 w-16">Severity</th>
                            <th className="text-left py-2 px-3 w-20">Rule</th>
                            <th className="text-left py-2 px-3">Resource</th>
                            <th className="text-left py-2 px-3">Finding</th>
                            <th className="text-left py-2 px-3">Recommendation</th>
                            <th className="text-center py-2 px-3 w-24"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {recsData?.recommendations.map((r) => {
                            const source = extractSource(r.relatedView);
                            const tab = extractTab(r.relatedView);
                            const canNavigate = !!onNavigate && !!source;
                            return (
                                <tr
                                    key={r.id}
                                    className={`border-b border-semantic-border-secondary/50 transition-colors
                                        ${canNavigate ? 'hover:bg-semantic-bg-secondary/50 cursor-pointer' : 'hover:bg-semantic-bg-secondary/30'}`}
                                    onClick={canNavigate ? () => onNavigate(tab, source) : undefined}
                                >
                                    <td className="py-2 px-3">
                                        <SeverityBadge severity={r.severity} />
                                    </td>
                                    <td className="py-2 px-3">
                                        <Tooltip text={RULE_META[r.rule]?.tooltip || r.rule}>
                                            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-semantic-bg-secondary text-semantic-text-secondary">
                                                {RULE_META[r.rule]?.label || r.rule}
                                            </span>
                                        </Tooltip>
                                    </td>
                                    <td className="py-2 px-3 font-mono text-semantic-text-primary">{r.resource}</td>
                                    <td className="py-2 px-3 text-semantic-text-primary">{r.message}</td>
                                    <td className="py-2 px-3 text-semantic-text-secondary">{r.action}</td>
                                    <td className="py-2 px-3 text-center">
                                        {canNavigate && (
                                            <span className="flex items-center justify-center gap-1 text-semantic-text-brand">
                                                <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M6 4l4 4-4 4" />
                                                </svg>
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {(!recsData || recsData.recommendations.length === 0) && (
                            <tr>
                                <td colSpan={6} className="py-8 text-center text-semantic-text-secondary">
                                    No recommendations
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Srv6RecommendationsTab;
